<script setup>
import { computed, ref, shallowRef, watch } from "vue";
import { useStore } from "vuex";
import UiBadge from "@/components/ui/UiBadge.vue";
import UiBarListCard from "@/components/ui/UiBarListCard.vue";
import UiDetailList from "@/components/ui/UiDetailList.vue";
import UiDialog from "@/components/ui/UiDialog.vue";
import UiFormDialog from "@/components/ui/UiFormDialog.vue";
import UiPageHeader from "@/components/ui/UiPageHeader.vue";
import UiPerson from "@/components/ui/UiPerson.vue";
import UiStatCard from "@/components/ui/UiStatCard.vue";
import UiTableCard from "@/components/ui/UiTableCard.vue";
import LardoAlertDialog from "@/components/dashboard/LardoAlertDialog.vue";
import { apiRequest, errorMessage } from "@/api/http";
import { useAsyncAction } from "@/composables/useAsyncAction";
import { gradeRisk, lardoSeverity } from "@/constants/risk";
import { reportStatus } from "@/constants/reports";

// Teacher/adviser dashboard: class totals, students at risk of failing,
// LARDO alerts (with the action taken for each) and misbehavior reports.
const RISK = "#ef4444";
const LARDO = "#f59e0b";
const CONFERENCE = "Parent-Teacher Conference + Intensive Intervention";

const store = useStore();
const year = computed(() => store.getters.getFilterSelected);
const assignedModuleID = computed(() => localStorage.getItem("AssignedModID"));

const data = shallowRef({});
const loading = shallowRef(false);
const error = shallowRef("");

async function load() {
  if (!year.value) return;
  loading.value = true;
  error.value = "";

  try {
    data.value =
      (await apiRequest(
        "get",
        `/enroll-student/getFacultyDashboardData/${year.value}/${assignedModuleID.value}`,
      )) || {};
  } catch (e) {
    error.value = errorMessage(e, "Could not load the dashboard.");
  } finally {
    loading.value = false;
  }
}
watch(year, load, { immediate: true });

const studentCount = computed(() => Number(data.value.studentCount) || 0);
const atRiskCount = computed(() => Number(data.value.atRiskCount) || 0);
const lardoCount = computed(() => Number(data.value.lardoCount) || 0);
const atRisk = computed(() => data.value.atRiskStudents ?? []);
const lardo = computed(() =>
  (data.value.lardoStudents ?? []).map((a) => ({
    ...a,
    pending: !a.action_taken,
  })),
);
const misbehavior = computed(() =>
  (data.value.misbehaveList ?? []).map((m, i) => ({ ...m, key: i })),
);

const percent = (n) =>
  studentCount.value
    ? Math.min(100, Math.round((n / studentCount.value) * 100))
    : 0;

const overviewRows = computed(() => [
  { label: "Total students", value: studentCount.value },
  { label: "At risk", value: atRiskCount.value },
  { label: "LARDO", value: lardoCount.value },
]);
const riskRows = computed(() =>
  ["High", "Moderate", "Passable"].map((label) => ({
    label,
    value: atRisk.value.filter(
      (s) => gradeRisk(s.transmuted_grade).label === label,
    ).length,
  })),
);

const atRiskHeaders = [
  { title: "Student", key: "name" },
  { title: "Grade", key: "transmuted_grade" },
  { title: "Recommendation", key: "remarks" },
  { title: "Action taken", key: "action_taken", sortable: false },
];
const lardoHeaders = [
  { title: "Student", key: "name" },
  { title: "Alert", key: "remarks" },
  { title: "Recommendation", key: "recommendation" },
  { title: "Action taken", key: "action_taken", sortable: false },
];
const misbehaviorHeaders = [
  { title: "Student", key: "name" },
  { title: "Status", key: "status" },
];

// At-risk details: students failing several subjects get the full list.
const riskOpen = shallowRef(false);
const riskStudent = shallowRef(null);
const riskSubjects = shallowRef(null);

async function openRisk(student) {
  riskStudent.value = student;
  riskSubjects.value = null;
  riskOpen.value = true;
  if (student.remarks !== CONFERENCE) return;
  try {
    riskSubjects.value = await apiRequest(
      "get",
      `/enroll-student/getAllSubjectThatAtRisk/${year.value}/${student.id}`,
    );
  } catch {
    riskSubjects.value = [];
  }
}

const lardoOpen = shallowRef(false);
const lardoAlert = shallowRef(null);
function openLardo(alert) {
  lardoAlert.value = alert;
  lardoOpen.value = true;
}

// Action taken, shared by both tables.
const actionOpen = shallowRef(false);
const actionRow = shallowRef(null);
const actionType = shallowRef("at_risk"); // 'at_risk' | 'lardo'
const actionForm = ref({ text: "" });
const actionFields = computed(() => [
  {
    key: "text",
    label: "What was done?",
    type: "textarea",
    required: true,
    placeholder:
      "e.g. Met with the student and parent to set a remedial schedule",
    description: actionRecommendation.value
      ? `Recommended: ${actionRecommendation.value}`
      : "",
    rules: [
      (v) => String(v || "").length <= 300 || "Keep it under 300 characters.",
    ],
  },
]);
const actionRecommendation = computed(() =>
  actionRow.value
    ? actionType.value === "lardo"
      ? actionRow.value.recommendation
      : actionRow.value.remarks
    : "",
);

function openAction(row, type) {
  actionRow.value = row;
  actionType.value = type;
  actionForm.value = { text: row.action_taken || "" };
  actionOpen.value = true;
}

const { busy, run } = useAsyncAction();

async function saveAction(value = actionForm.value.text.trim()) {
  const row = actionRow.value;
  const payload = {
    id: row.id,
    type: actionType.value,
    action_taken: value,
    recommendation: actionRecommendation.value,
  };
  const ok = await run(
    () =>
      apiRequest("patch", `/enroll-student/updateActionTaken/${row.atriskID}`, {
        data: JSON.stringify(payload),
      }),
    {
      success: value ? "Action saved." : "Action removed.",
      error: "Could not save the action.",
    },
  );
  if (ok) {
    actionOpen.value = false;
    load();
  }
}
</script>

<template>
  <div class="page-stack">
    <UiPageHeader
      title="Dashboard"
      subtitle="How your students are doing this school year."
    >
      <template #actions>
        <v-btn
          variant="text"
          icon="mdi-refresh"
          size="small"
          :loading="loading"
          aria-label="Refresh"
          @click="load"
        />
      </template>
    </UiPageHeader>

    <v-alert v-if="error" type="error" variant="tonal" density="compact">{{
      error
    }}</v-alert>

    <section class="stat-grid" aria-label="Class totals">
      <UiStatCard
        title="Students"
        icon="mdi-account-group-outline"
        :value="studentCount"
        description="In your classes"
        :loading="loading"
      />
      <UiStatCard
        title="At risk of failing"
        icon="mdi-alert-circle-outline"
        :value="atRiskCount"
        :progress="percent(atRiskCount)"
        :tone="RISK"
        :description="`${percent(atRiskCount)}% have a grade of 80 or below`"
        :loading="loading"
      />
      <UiStatCard
        title="LARDO"
        icon="mdi-file-document-alert-outline"
        :value="lardoCount"
        :progress="percent(lardoCount)"
        :tone="LARDO"
        description="Flagged from attendance"
        :loading="loading"
      />
    </section>

    <section class="chart-grid">
      <UiBarListCard
        title="Overview"
        description="All students compared with flagged students."
        :rows="overviewRows"
        :series="[{ key: 'value', label: 'Students', color: 'var(--brand)' }]"
        :loading="loading"
        empty-text="Stats appear once students are enrolled."
      />
      <UiBarListCard
        title="Risk levels"
        description="At-risk students by grade band."
        :rows="riskRows"
        :series="[{ key: 'value', label: 'Students', color: RISK }]"
        :loading="loading"
        empty-text="No at-risk students right now."
      />
    </section>

    <UiTableCard
      title="At risk of failing"
      description="Students with a grade of 80 or below."
      :headers="atRiskHeaders"
      :items="atRisk"
      :loading="loading"
      search-placeholder="Search name or LRN"
      :search-fields="['lrn']"
      empty-text="Everyone is on track."
      row-label="name"
      :row-actions="
        (row) => [
          {
            title: 'View details',
            icon: 'mdi-file-document-outline',
            onClick: () => openRisk(row),
          },
          {
            title: row.action_taken ? 'Edit action' : 'Log action',
            icon: 'mdi-pencil-outline',
            onClick: () => openAction(row, 'at_risk'),
          },
        ]
      "
    >
      <template #[`item.name`]="{ item }">
        <UiPerson
          :name="item.name"
          :subtitle="item.lrn ? `LRN ${item.lrn}` : ''"
        />
      </template>
      <template #[`item.transmuted_grade`]="{ item }">
        <UiBadge :variant="gradeRisk(item.transmuted_grade).variant">
          {{ item.transmuted_grade ?? "—" }} ·
          {{ gradeRisk(item.transmuted_grade).label }}
        </UiBadge>
      </template>
      <template #[`item.remarks`]="{ item }">
        <span class="cell-wrap">{{ item.remarks }}</span>
      </template>
      <template #[`item.action_taken`]="{ item }">
        <button
          type="button"
          class="action-cell"
          :class="{ 'action-cell--empty': !item.action_taken }"
          @click="openAction(item, 'at_risk')"
        >
          {{ item.action_taken || "Log action" }}
        </button>
      </template>
    </UiTableCard>

    <UiTableCard
      title="Learners at risk of dropping out"
      description="LARDO alerts raised from attendance."
      :headers="lardoHeaders"
      :items="lardo"
      :loading="loading"
      search-placeholder="Search name or LRN"
      :search-fields="['lrn']"
      empty-text="New alerts appear here as attendance is recorded."
      row-label="name"
      :row-actions="
        (row) => [
          {
            title: 'View details',
            icon: 'mdi-file-document-outline',
            onClick: () => openLardo(row),
          },
          {
            title: row.action_taken ? 'Edit action' : 'Log action',
            icon: 'mdi-pencil-outline',
            onClick: () => openAction(row, 'lardo'),
          },
        ]
      "
    >
      <template #[`item.name`]="{ item }">
        <UiPerson
          :name="item.name"
          :subtitle="item.lrn ? `LRN ${item.lrn}` : ''"
        />
      </template>
      <template #[`item.remarks`]="{ item }">
        <UiBadge :variant="lardoSeverity(item.remarks).variant">{{
          lardoSeverity(item.remarks).label
        }}</UiBadge>
      </template>
      <template #[`item.recommendation`]="{ item }">
        <span class="cell-wrap">{{ item.recommendation }}</span>
      </template>
      <template #[`item.action_taken`]="{ item }">
        <button
          type="button"
          class="action-cell"
          :class="{ 'action-cell--empty': !item.action_taken }"
          @click="openAction(item, 'lardo')"
        >
          {{ item.action_taken || "Log action" }}
        </button>
      </template>
    </UiTableCard>

    <UiTableCard
      title="Misbehavior reports"
      description="Reports about your students and where they are in review."
      :headers="misbehaviorHeaders"
      :items="misbehavior"
      item-value="key"
      :loading="loading"
      :searchable="false"
      empty-text="Nothing reported for this period."
    >
      <template #[`item.name`]="{ item }">
        <UiPerson :name="item.name" />
      </template>
      <template #[`item.status`]="{ item }">
        <UiBadge :variant="reportStatus(item.status).variant">{{
          reportStatus(item.status).name
        }}</UiBadge>
      </template>
    </UiTableCard>

    <UiDialog
      v-model="riskOpen"
      :title="riskStudent?.name || 'Student'"
      :description="riskStudent?.lrn ? `LRN ${riskStudent.lrn}` : ''"
      size="md"
      :persistent="false"
    >
      <div v-if="riskStudent" class="form-stack">
        <UiDetailList
          :items="[
            { label: 'Grade level', value: riskStudent.grade_level },
            {
              label: 'Section',
              value: riskStudent.room_name && riskStudent.room_name.toUpperCase(),
            },
          ]"
        />
        <template v-if="riskStudent.remarks === CONFERENCE">
          <v-alert type="error" variant="tonal" density="compact">
            Failing several subjects — schedule a parent-teacher conference as
            soon as possible.
          </v-alert>
          <v-progress-linear v-if="!riskSubjects" indeterminate />
          <div
            v-for="subject in riskSubjects || []"
            :key="subject.id"
            class="subject"
          >
            <div class="subject__head">
              <span class="font-weight-medium">{{
                subject.subject_title
              }}</span>
              <UiBadge :variant="gradeRisk(subject.transmuted_grade).variant">{{
                subject.transmuted_grade
              }}</UiBadge>
            </div>
            <p class="subject__meta">
              {{
                [subject.grade_level, subject.room_name]
                  .filter(Boolean)
                  .join(" · ")
              }}
            </p>
            <p class="subject__meta">{{ subject.remarks }}</p>
          </div>
        </template>
        <UiDetailList
          v-else
          :items="[
            { label: 'Subject', value: riskStudent.subject_title },
            { label: 'Grade', value: riskStudent.transmuted_grade },
            {
              label: 'Recommended support',
              value: riskStudent.remarks,
              span: 2,
            },
            { label: 'Action taken', value: riskStudent.action_taken, span: 2 },
          ]"
        />
      </div>
      <template #footer="{ close }">
        <v-btn variant="outlined" @click="close">Close</v-btn>
      </template>
    </UiDialog>

    <LardoAlertDialog v-model="lardoOpen" :alert="lardoAlert" />

    <UiFormDialog
      v-model="actionOpen"
      :title="
        actionRow?.action_taken ? 'Edit action taken' : 'Log action taken'
      "
      :description="actionRow?.name"
      size="md"
      v-model:values="actionForm"
      :fields="actionFields"
      :saving="busy"
      submit-text="Save"
      @submit="saveAction()"
    >
      <template v-if="actionRow?.action_taken" #footer-start>
        <v-btn
          variant="text"
          color="error"
          class="mr-auto"
          :disabled="busy"
          @click="saveAction('')"
          >Remove</v-btn
        >
      </template>
    </UiFormDialog>
  </div>
</template>

<style scoped>
.cell-wrap {
  display: inline-block;
  max-width: 320px;
  white-space: normal;
  line-height: 1.4;
  padding: 6px 0;
}
.action-cell {
  max-width: 240px;
  padding: 4px 8px;
  margin-left: -8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--foreground);
  font: inherit;
  font-size: 13px;
  text-align: left;
  white-space: normal;
  cursor: pointer;
}
.action-cell:hover {
  background-color: var(--accent);
}
.action-cell--empty {
  color: var(--muted-foreground);
}
.subject {
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
}
.subject__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.subject__meta {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
</style>
